import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthGuard } from '../components/AuthGuard';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { fetchCoaches } from '@/services/coachService';
import { formatDateString } from '@/lib/utils';

interface PlayerEvaluation {
  id?: string;
  coach_id: string;
  evaluation_id: string;
  player_id?: string;
  ratings?: { [key: string]: number };
  rating_average?: number;
  rating_count?: number;
  notes?: string;
  team_recommendations?: string[];
  updated_at?: string;
  [key: string]: any;
}

const PlayerDetails: React.FC = () => {
  const { playerId, teamId } = useParams<{ playerId: string, teamId: string }>();
  const navigate = useNavigate();

  // State from PlayerDialog logic
  const [evaluations, setEvaluations] = useState<PlayerEvaluation[]>([]);
  const [loadingEvals, setLoadingEvals] = useState(false);
  const [evalError, setEvalError] = useState<string | null>(null);
  const [coachIdToName, setCoachIdToName] = useState<Record<string, string>>({});
  const [teamOffers, setTeamOffers] = useState<any[]>([]);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [offersError, setOffersError] = useState<string | null>(null);
  // Notes from team_player_notes
  const [playerNotes, setPlayerNotes] = useState<string>("");
  const [playerBirthdate, setPlayerBirthdate] = useState<string | null>(null);
  const [playerName, setPlayerName] = useState<string | null>(null);
  const [playerProfile, setPlayerProfile] = useState<any>({});
  const [editNotes, setEditNotes] = useState<string>("");
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Parent info state
  const [registeredParents, setRegisteredParents] = useState<any[]>([]);
  const [invitedParents, setInvitedParents] = useState<any[]>([]);
  const [loadingParents, setLoadingParents] = useState(false);
  const [parentsError, setParentsError] = useState<string | null>(null);

  // Fetch parent info for this player
  useEffect(() => {
    async function fetchParents() {
      if (!playerId) return;
      setLoadingParents(true);
      setParentsError(null);
      try {
        // 1. Registered parents
        const { data: rels, error: relsError } = await supabase
          .from('player_parent_relationships')
          .select('parent_id')
          .eq('player_id', playerId);
        if (relsError) throw relsError;
        const parentIds = rels.map((r: any) => r.parent_id);
        let profiles: any[] = [];
        if (parentIds.length > 0) {
          const { data: profilesData, error: profilesError } = await supabase
            .from('profiles')
            .select('id, first_name, last_name, email, phone_number')
            .in('id', parentIds);
          if (profilesError) throw profilesError;
          profiles = profilesData;
        }
        setRegisteredParents(profiles);

        // 2. Invited parents (pending sign up)
        const { data: invitationPlayers, error: ipError } = await supabase
          .from('invitation_players')
          .select('invitation_id')
          .eq('player_id', playerId);
        if (ipError) throw ipError;
        const invitationIds = invitationPlayers.map((ip: any) => ip.invitation_id);
        let invited: any[] = [];
        if (invitationIds.length > 0) {
          const { data: invitations, error: invError } = await supabase
            .from('invitations')
            .select('id, email, first_name, last_name, phone_number, status')
            .eq('status', 'pending')
            .in('id', invitationIds);
          if (invError) throw invError;
          invited = invitations;
        }
        setInvitedParents(invited);
      } catch (err: any) {
        setParentsError(err.message || 'Failed to load parent information');
      } finally {
        setLoadingParents(false);
      }
    }
    fetchParents();
  }, [playerId]);

  // Fetch team player notes for this player/team
  useEffect(() => {
    async function fetchTeamPlayerNote() {
      if (!playerId || !teamId) return;
      const { data, error } = await supabase
        .from('team_player_notes')
        .select('notes')
        .eq('player_id', playerId)
        .eq('team_id', teamId)
        .single();
      if (!error && data) {
        setPlayerNotes(data.notes);
        setEditNotes(data.notes);
      } else {
        setPlayerNotes("");
        setEditNotes("");
      }
    }
    fetchTeamPlayerNote();
    setIsEditing(false);
    setSaveSuccess(false);
    setSaveError(null);
  }, [playerId, teamId]);

  // Save handler for notes
  async function handleSaveNotes() {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const { error } = await supabase
        .from('team_player_notes')
        .upsert({
          player_id: playerId,
          team_id: teamId,
          notes: editNotes,
          note_type: 'coach',
          updated_at: new Date().toISOString(),
        }, { onConflict: 'player_id,team_id' });
      if (error) throw error;
      setPlayerNotes(editNotes);
      setIsEditing(false);
      setSaveSuccess(true);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save notes');
    } finally {
      setSaving(false);
    }
  }

  // Fetch team offers for this player
  useEffect(() => {
    async function fetchTeamOffers() {
      if (!playerId) return;
      setLoadingOffers(true);
      setOffersError(null);
      const { data, error } = await supabase
        .from('team_players')
        .select('id, team_id, player_id, status, created_at, updated_at, teams (id, name, sex, description)')
        .eq('player_id', playerId);
      if (error) setOffersError(error.message);
      else setTeamOffers(data || []);
      setLoadingOffers(false);
    }
    fetchTeamOffers();
  }, [playerId]);

  // Fetch evaluations
  useEffect(() => {
    async function fetchEvaluations() {
      if (!playerId) return;
      setLoadingEvals(true);
      setEvalError(null);
      const { data, error } = await supabase
        .from('player_evaluations')
        .select('*')
        .eq('player_id', playerId)
        .order('updated_at', { ascending: false });
      if (error) setEvalError(error.message);
      else setEvaluations((data as PlayerEvaluation[]) || []);
      setLoadingEvals(false);
    }
    fetchEvaluations();
  }, [playerId]);

  // Fetch coaches and build coachIdToName map after evaluations are loaded
  useEffect(() => {
    async function loadCoachNames() {
      if (!evaluations.length) return;
      const coaches = await fetchCoaches();
      const map: Record<string, string> = {};
      coaches.forEach(coach => {
        map[coach.id] = coach.name ?? coach.id;
      });
      setCoachIdToName(map);
    }
    loadCoachNames();
  }, [evaluations]);

  // Optionally fetch player name if available in your schema
  useEffect(() => {
    async function fetchPlayerProfile() {
      if (!playerId) return;
      const { data, error } = await supabase
        .from('players')
        .select('first_name, last_name, profile, birthdate')
        .eq('id', playerId)
        .single();
      if (!error && data) {
        setPlayerName(data.first_name + ' ' + data.last_name);
        setPlayerProfile(data.profile || {});
        setPlayerBirthdate(data.birthdate || null);
      }
    }
    fetchPlayerProfile();
  }, [playerId]);

  // Get current team id from the most recent offer (if needed)
  const currentTeamId = teamOffers.length > 0 ? teamOffers[0].team_id : null;

  return (
    <AuthGuard>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b flex items-center px-3 py-2">
        <button
          onClick={() => navigate(-1)}
          aria-label="Back"
          className="p-2 rounded hover:bg-gray-100 focus:outline-none mr-2"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <span className="text-lg font-semibold">Player Details{playerName ? `: ${playerName}` : ''}</span>
      </div>
      <div className="max-w-2xl mx-auto p-4">
        {/* Profile Section */}
        <section className="mb-8">
          <div className="rounded-xl border bg-white shadow-md p-6">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-800 mb-3">
                {playerName || <span className="italic text-gray-400">Player Name Not Available</span>}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border-l-2 border-indigo-500 pl-3">
                  <div className="text-sm text-gray-500 uppercase tracking-wider">Birthdate</div>
                  <div className="font-medium mt-1">
                    {playerBirthdate ? 
                      formatDateString(playerBirthdate, 'en-US', 'medium') : 
                      <span className="italic text-gray-400">Not Available</span>}
                  </div>
                </div>
                <div className="border-l-2 border-indigo-500 pl-3">
                  <div className="text-sm text-gray-500 uppercase tracking-wider">Desired Level</div>
                  <div className="font-medium mt-1">
                    {playerProfile.competitiveness ? 
                      (Array.isArray(playerProfile.competitiveness) ? 
                        playerProfile.competitiveness.join(', ') : 
                        playerProfile.competitiveness) : 
                      <span className="italic text-gray-400">Not Specified</span>}
                  </div>
                </div>
                <div className="border-l-2 border-indigo-500 pl-3">
                  <div className="text-sm text-gray-500 uppercase tracking-wider">Desired Positions</div>
                  <div className="font-medium mt-1">
                    {playerProfile.positions ? 
                      (Array.isArray(playerProfile.positions) ? 
                        playerProfile.positions.join(', ') : 
                        playerProfile.positions) : 
                      <span className="italic text-gray-400">Not Specified</span>}
                  </div>
                </div>
              </div>
              <div className="mt-6 border-l-2 border-indigo-500 pl-3">
                <div className="text-sm text-gray-500 uppercase tracking-wider">Parent Notes</div>
                <div className="font-medium mt-1 whitespace-pre-line">
                  {playerProfile.notes ? 
                    playerProfile.notes : 
                    <span className="italic text-gray-400">No notes provided</span>}
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Coach Notes (editable) */}
        <section className="mb-8">
          <h3 className="text-lg font-bold mb-2 text-gray-800 flex items-center gap-2">
            Coach Notes
            {!isEditing && (
              <button
                className="ml-2 px-2 py-1 text-xs rounded bg-indigo-100 text-indigo-700 border border-indigo-200 hover:bg-indigo-200"
                onClick={() => { setEditNotes(playerNotes); setIsEditing(true); }}
              >Edit</button>
            )}
          </h3>
          {isEditing ? (
            <>
              <textarea
                className="w-full min-h-[100px] border rounded p-2 text-gray-700"
                value={editNotes}
                onChange={e => setEditNotes(e.target.value)}
                disabled={saving}
              />
              <div className="mt-2 flex gap-2">
                <button
                  className="px-4 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                  onClick={handleSaveNotes}
                  disabled={saving}
                >{saving ? 'Saving...' : 'Save'}</button>
                <button
                  className="px-4 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
                  onClick={() => { setIsEditing(false); setEditNotes(playerNotes); }}
                  disabled={saving}
                >Cancel</button>
              </div>
              {saveError && <div className="mt-2 text-sm text-red-600">{saveError}</div>}
              {saveSuccess && <div className="mt-2 text-sm text-green-600">Notes saved!</div>}
            </>
          ) : (
            <div className="bg-gray-50 border rounded p-3 min-h-[100px] text-gray-700 whitespace-pre-line">
              {playerNotes || <span className="italic text-gray-400">No notes</span>}
            </div>
          )}
        </section>
        {/* Coach Evaluations & Team Recommendations */}
        <section className="mb-8">
          <h3 className="text-lg font-bold mb-4 border-b pb-1 text-gray-800">Coach Evaluations & Team Recommendations</h3>
          {loadingEvals ? (
            <div className="py-6 text-center text-gray-500">Loading evaluations...</div>
          ) : evalError ? (
            <div className="py-6 text-center text-red-500">{evalError}</div>
          ) : evaluations.length === 0 ? (
            <div className="py-6 text-center text-gray-400">No evaluations found for this player.</div>
          ) : (
            <div className="flex flex-col gap-6">
              {evaluations.map((evalItem, i) => (
                <div
                  key={evalItem.id || i}
                  className="rounded-xl border bg-white shadow-md px-6 py-4 flex flex-col gap-3 md:flex-row md:items-start md:gap-8"
                  style={{ borderLeft: '6px solid #6366f1' }}
                >
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-base font-semibold text-indigo-700">
                        {coachIdToName[evalItem.coach_id] || evalItem.coach_id}
                      </span>
                      <span className="text-xs text-gray-400">({evalItem.updated_at?.slice(0, 16).replace('T', ' ')})</span>
                    </div>
                    <div className="mb-2">
                      <span className="font-medium text-gray-700">Notes:</span>{' '}
                      {evalItem.notes ? (
                        <span className="text-gray-800">{evalItem.notes}</span>
                      ) : (
                        <span className="italic text-gray-400">No notes</span>
                      )}
                    </div>
                    <div className="mb-2">
                      <span className="font-medium text-gray-700">Ratings:</span>{' '}
                      {evalItem.ratings && Object.keys(evalItem.ratings).length > 0 ? (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {Object.entries(evalItem.ratings).map(([category, value]) => (
                            <span
                              key={category}
                              className="inline-flex items-center px-2 py-1 rounded-full bg-indigo-100 text-indigo-800 text-xs font-semibold border border-indigo-200"
                            >
                              <span className="font-mono mr-1">{category}</span>
                              <span className="ml-1">{value}</span>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="italic text-gray-400">No ratings</span>
                      )}
                      {(typeof evalItem.rating_average === 'number' || typeof evalItem.rating_count === 'number') && (
                        <div className="text-xs text-gray-500 mt-1">
                          {typeof evalItem.rating_average === 'number' && (
                            <>Average: <span className="font-semibold">{evalItem.rating_average.toFixed(2)}</span>{' '}</>
                          )}
                          {typeof evalItem.rating_count === 'number' && (
                            <>| Count: <span className="font-semibold">{evalItem.rating_count}</span></>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Team Recommendations:</span>{' '}
                      {evalItem.team_recommendations && evalItem.team_recommendations.length > 0
                        ? evalItem.team_recommendations.map((team, idx) => (
                            <span
                              key={team}
                              className="inline-block bg-green-100 text-green-800 text-xs font-semibold rounded px-2 py-1 mr-2 border border-green-200"
                            >
                              {team}
                            </span>
                          ))
                        : <span className="italic text-gray-400">None</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
        {/* Team Offers Section */}
        <section className="mb-8">
          <h3 className="text-lg font-bold mb-4 border-b pb-1 text-gray-800">Team Offers</h3>
          {loadingOffers ? (
            <div className="py-6 text-center text-gray-500">Loading team offers...</div>
          ) : offersError ? (
            <div className="py-6 text-center text-red-500">{offersError}</div>
          ) : teamOffers.length === 0 ? (
            <div className="py-6 text-center text-gray-400">No team offers found for this player.</div>
          ) : (
            <div className="flex flex-col gap-4">
              {/* Current team offer first, if exists */}
              {teamOffers.filter(offer => offer.team_id === currentTeamId).map(offer => (
                <div key={offer.id} className="rounded-xl border-2 border-indigo-600 bg-indigo-50 px-5 py-3">
                  <div className="flex flex-col md:flex-row md:items-center md:gap-4">
                    <span className="font-semibold text-indigo-800 text-base">
                      {offer.teams?.name || offer.team_id} (Current Team)
                    </span>
                    <span className="ml-2 text-xs text-gray-500">{offer.updated_at?.slice(0, 16).replace('T', ' ')}</span>
                  </div>
                  <div className="mt-1 text-sm">
                    <span className="font-medium text-gray-700">Status:</span> <span className="font-mono px-2 py-1 rounded bg-indigo-100 text-indigo-800 border border-indigo-200">{offer.status}</span>
                  </div>
                </div>
              ))}
              {/* Other teams' offers */}
              {teamOffers.filter(offer => offer.team_id !== currentTeamId).map(offer => (
                <div key={offer.id} className="rounded-xl border bg-white shadow px-5 py-3">
                  <div className="flex flex-col md:flex-row md:items-center md:gap-4">
                    <span className="font-semibold text-gray-800 text-base">
                      {offer.teams?.name || offer.team_id}
                    </span>
                    <span className="ml-2 text-xs text-gray-500">{offer.updated_at?.slice(0, 16).replace('T', ' ')}</span>
                  </div>
                  <div className="mt-1 text-sm">
                    <span className="font-medium text-gray-700">Status:</span> <span className="font-mono px-2 py-1 rounded bg-gray-100 text-gray-800 border border-gray-200">{offer.status}</span>
                  </div>
                  {offer.teams?.description && (
                    <div className="mt-1 text-xs text-gray-600 italic">{offer.teams.description}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      {/* Parents Section */}
      <section className="mb-8">
        <h3 className="text-lg font-bold mb-4 border-b pb-1 text-gray-800">Parents</h3>
        {loadingParents ? (
          <div className="py-6 text-center text-gray-500">Loading parent information...</div>
        ) : parentsError ? (
          <div className="py-6 text-center text-red-500">{parentsError}</div>
        ) : (
          <>
            {registeredParents.length === 0 && invitedParents.length === 0 ? (
              <div className="py-6 text-center text-gray-400">No parents or invitations found for this player.</div>
            ) : (
              <div className="flex flex-col gap-4">
                {registeredParents.length > 0 && (
                  <div>
                    <div className="font-semibold text-gray-700 mb-2">Registered Parents:</div>
                    <ul className="divide-y divide-gray-200">
                      {registeredParents.map((parent) => (
                        <li key={parent.id} className="py-2 flex flex-col md:flex-row md:items-center md:gap-6">
                          <span className="font-medium text-gray-900">{parent.first_name} {parent.last_name}</span>
                          <span className="ml-2 text-sm text-gray-600">{parent.email}</span>
                          {parent.phone_number && <span className="ml-2 text-sm text-gray-500">{parent.phone_number}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {invitedParents.length > 0 && (
                  <div>
                    <div className="font-semibold text-gray-700 mb-2">Invited Parents (Pending):</div>
                    <ul className="divide-y divide-gray-200">
                      {invitedParents.map((inv) => (
                        <li key={inv.id} className="py-2 flex flex-col md:flex-row md:items-center md:gap-6 opacity-70">
                          <span className="font-medium text-gray-900">{inv.first_name} {inv.last_name}</span>
                          <span className="ml-2 text-sm text-gray-600">{inv.email}</span>
                          {inv.phone_number && <span className="ml-2 text-sm text-gray-500">{inv.phone_number}</span>}
                          <span className="ml-2 text-xs text-yellow-600 bg-yellow-50 border border-yellow-200 rounded px-2 py-0.5">Invitation Pending</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </section>
      </div>
    </AuthGuard>
  );
};

export default PlayerDetails;
